import { useCallback } from 'react';
import { formatMoney, getCurrencySymbol } from 'utils/moneyFormatter';

/**
 * Hook that returns a currency formatter function based on current company settings
 * The currency is read from Redux state: userInfo.company.company_settings.currency
 *
 * @returns Memoized format function that uses the current currency from Redux
 *
 * @example
 * const formatCurrency = useFormatMoney();
 * return <div>{formatCurrency(199.99)}</div>; // "€199,99" or "$199.99" depending on company settings
 * supplierCurrency - currency from endpoint
 */
export const useFormatMoney = (currency?: string) => {
  const format = useCallback(
    (value: number | undefined | null): string => {
      return formatMoney(value, currency || 'EUR');
    },
    [currency]
  );

  return format;
};

/**
 * Hook that returns the current currency symbol based on company settings
 * Useful when you need just the symbol (e.g., for labels, icons)
 *
 * @example
 * const currencySymbol = useCurrencySymbol();
 * return <span>{currencySymbol}</span>; // "€" or "$" depending on company settings
 */
export const useCurrencySymbol = (currency?: string) => {
  return getCurrencySymbol(currency || 'EUR');
};

export default useFormatMoney;
